import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MockAuthProvider } from './contexts/MockAuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './components/LoginPage';
import CustomerDashboard from './components/customer/CustomerDashboard';
import NearbyShops from './components/customer/NearbyShops';
import ShopList from './components/customer/ShopList';
import RewardsHistory from './components/customer/RewardsHistory';
import BusinessDashboard from './components/business/BusinessDashboard';
import CreditByMobilePage from './components/business/CreditByMobilePage';
import BusinessProfile from './components/business/BusinessProfile';
import CustomerManagement from './components/business/CustomerManagement';
import SalesHistory from './components/business/SalesHistory';
import ManageOffers from './components/business/ManageOffers';
import ClaimRequestsPage from './components/business/ClaimRequestsPage';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminBusinessList from './components/admin/AdminBusinessList';
import AdminBusinessDetails from './components/admin/AdminBusinessDetails';
import AdminUserManagement from './components/admin/AdminUserManagement';
import AdminApproval from './components/admin/AdminApproval';
import FindMyShop from './components/customer/FindMyShop';

// Role-based redirect component
const RoleBasedRedirect = () => {
  const { currentUser, userRole } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  switch (userRole) {
    case 'customer':
      return <Navigate to="/customer/dashboard" replace />;
    case 'business':
      return <Navigate to="/business/dashboard" replace />;
    case 'admin':
      return <Navigate to="/admin/dashboard" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

// Toggle between real Firebase auth and mock auth
const USE_MOCK_AUTH = false;

function App() {
  return (
    <AuthProvider>
      <MockAuthProvider>
        <ThemeProvider>
        <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/admin" element={<AdminLogin />} />
              
              {/* Admin Routes */}
              <Route 
                path="/admin/dashboard" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/business" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminBusinessList />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/business/:profileId" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminBusinessDetails />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/users" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminUserManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/approval" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminApproval />
                  </ProtectedRoute>
                } 
              />
              
              {/* Customer Routes */}
              <Route 
                path="/customer/dashboard" 
                element={
                  <ProtectedRoute requiredRole="customer">
                    <CustomerDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/customer/nearby-shops" 
                element={
                  <ProtectedRoute requiredRole="customer">
                    <FindMyShop />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/customer/shops" 
                element={
                  <ProtectedRoute requiredRole="customer">
                    <ShopList />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/customer/rewards-history" 
                element={
                  <ProtectedRoute requiredRole="customer">
                    <RewardsHistory />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/customer/offers" 
                element={
                  <ProtectedRoute requiredRole="customer">
                    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Available Offers</h1>
                        <p className="text-gray-600">This page is under development</p>
                      </div>
                    </div>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/customer/purchase-history" 
                element={
                  <ProtectedRoute requiredRole="customer">
                    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Purchase History</h1>
                        <p className="text-gray-600">This page is under development</p>
                      </div>
                    </div>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/customer/savings-history" 
                element={
                  <ProtectedRoute requiredRole="customer">
                    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Savings History</h1>
                        <p className="text-gray-600">This page is under development</p>
                      </div>
                    </div>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/customer/activity" 
                element={
                  <ProtectedRoute requiredRole="customer">
                    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Activity History</h1>
                        <p className="text-gray-600">This page is under development</p>
                      </div>
                    </div>
                  </ProtectedRoute>
                } 
              />

              {/* Business Routes */}
              <Route 
                path="/business/dashboard" 
                element={
                  <ProtectedRoute requiredRole="business">
                    <BusinessDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/business/credit-by-mobile" 
                element={
                  <ProtectedRoute requiredRole="business">
                    <CreditByMobilePage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/business/profile" 
                element={
                  <ProtectedRoute requiredRole="business">
                    <BusinessProfile />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/business/offers" 
                element={
                  <ProtectedRoute requiredRole="business">
                    <ManageOffers />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/business/customers" 
                element={
                  <ProtectedRoute requiredRole="business">
                    <CustomerManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/business/analytics" 
                element={
                  <ProtectedRoute requiredRole="business">
                    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Analytics</h1>
                        <p className="text-gray-600">This page is under development</p>
                      </div>
                    </div>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/business/sales" 
                element={
                  <ProtectedRoute requiredRole="business">
                    <SalesHistory />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/business/claim-requests" 
                element={
                  <ProtectedRoute requiredRole="business">
                    <ClaimRequestsPage />
                  </ProtectedRoute>
                } 
              />

              {/* Default Routes with Role-based Redirection */}
              <Route path="/" element={<RoleBasedRedirect />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>

            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  theme: {
                    primary: '#4ade80',
                    secondary: '#000',
                  },
                },
                error: {
                  duration: 4000,
                  theme: {
                    primary: '#ef4444',
                    secondary: '#000',
                  },
                },
              }}
            />
          </div>
        </Router>
        </ThemeProvider>
      </MockAuthProvider>
    </AuthProvider>
  );
}

export default App;
